import { useCallback, useMemo } from 'react'
import {
  useGridSelector,
  gridPaginationSelector,
  useGridApiContext,
  useGridRootProps,
  gridFilteredTopLevelRowCountSelector,
} from '@mui/x-data-grid-pro'
import { TablePaginationProps } from '@mui/material/TablePagination'

type GridPaginationProps = Pick<
  TablePaginationProps,
  | 'page'
  | 'onPageChange'
  | 'count'
  | 'rowsPerPage'
  | 'rowsPerPageOptions'
  | 'onRowsPerPageChange'
> & {
  disableNextButton: boolean
  disablePrevButton: boolean
}

export function useActions(disableInteraction: boolean): GridPaginationProps {
  const apiRef = useGridApiContext()
  const rootProps = useGridRootProps()
  const paginationState = useGridSelector(apiRef, gridPaginationSelector)

  const visibleTopLevelRowCount = useGridSelector(
    apiRef,
    gridFilteredTopLevelRowCountSelector,
  )

  const lastPage = useMemo(
    () =>
      Math.floor(
        rootProps.rowCount ??
          visibleTopLevelRowCount /
            (paginationState.paginationModel.pageSize || 1),
      ),
    [
      paginationState.paginationModel.pageSize,
      rootProps.rowCount,
      visibleTopLevelRowCount,
    ],
  )

  const page =
    paginationState.paginationModel.page <= lastPage
      ? paginationState.paginationModel.page
      : lastPage

  const onPageChange = useCallback(
    (_e, page: number) => apiRef.current.setPage(page),
    [apiRef],
  )

  const options = useGridRootProps()
  const pageSizeOptions = useMemo(
    () =>
      options.pageSizeOptions &&
      options.pageSizeOptions.indexOf(
        paginationState.paginationModel.pageSize,
      ) > -1
        ? options.pageSizeOptions
        : [],
    [options.pageSizeOptions, paginationState.paginationModel.pageSize],
  )

  const onRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      apiRef.current.setPageSize(Number(event.target.value)),
    [apiRef],
  )

  const disableNextButton = disableInteraction || page === lastPage
  const disablePrevButton = disableInteraction || page === 0

  return {
    count: rootProps.rowCount ?? visibleTopLevelRowCount,
    page,
    onPageChange,
    rowsPerPage: paginationState.paginationModel.pageSize,
    onRowsPerPageChange,
    rowsPerPageOptions: [...pageSizeOptions],
    disableNextButton,
    disablePrevButton,
  }
}
